import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { API, BACKEND } from "@/lib/api";

const TEAM_PLANS = ["pro", "scale", "enterprise"];

const inputCls =
    "rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none";

export default function TeamTab({ onViewPlans }) {
    const { user } = useAuth();
    const plan = user?.plan || "developer";
    const allowed = TEAM_PLANS.includes(plan) && !user?.org_id;
    const [members, setMembers] = useState([]);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [invite, setInvite] = useState(null);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await fetch(`${API}/auth/team`, { credentials: "include" });
            if (r.ok) setMembers((await r.json()).members || []);
        } catch {
            /* best-effort */
        }
    }, []);

    useEffect(() => {
        if (allowed) load();
    }, [allowed, load]);

    const send = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/auth/team/invite`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim(), name: name.trim() }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            setInvite(d);
            setEmail("");
            setName("");
            await load();
            toast.success("Teammate added");
        } catch (e) {
            toast.error("Invite failed", { description: String(e.message) });
        } finally {
            setBusy(false);
        }
    };

    const remove = async (memberId) => {
        try {
            await fetch(`${API}/auth/team/${memberId}`, {
                method: "DELETE",
                credentials: "include",
            });
            await load();
            toast.success("Member removed");
        } catch {
            toast.error("Remove failed");
        }
    };

    const copyPassword = async () => {
        try {
            await navigator.clipboard.writeText(invite.temp_password);
            setCopied(true);
            toast.success("Password copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    if (!allowed) {
        return (
            <div
                data-testid="team-upsell"
                className="glass-panel flex flex-col items-center rounded-xl p-10 text-center"
            >
                <Users className="h-8 w-8 text-cyan-500/60" />
                <h3 className="font-display mt-4 text-lg font-bold text-slate-100">
                    Teams are a Pro feature
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-400">
                    Invite teammates to share apps, dashboards, and API keys. Upgrade to Pro to
                    unlock team workspaces.
                </p>
                <button
                    data-testid="team-view-plans-button"
                    onClick={onViewPlans}
                    className="mt-6 rounded-md bg-cyan-400 px-6 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300"
                >
                    View plans
                </button>
            </div>
        );
    }

    return (
        <div data-testid="settings-team-tab" className="space-y-4">
            {invite && (
                <div
                    data-testid="team-invite-banner"
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-teal-500/30 bg-teal-950/30 px-5 py-4"
                >
                    <div className="min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-400">
                            {invite.member.email} added — share this password (shown once)
                        </div>
                        <code className="mt-1 block truncate font-mono text-xs text-teal-200">
                            {invite.temp_password}
                        </code>
                        <p className="mt-1 font-mono text-[10px] text-slate-500">
                            No email was sent — pass it to them directly.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            data-testid="team-invite-copy"
                            onClick={copyPassword}
                            className="flex items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-300 hover:border-teal-500/40"
                        >
                            {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                            {copied ? "copied" : "copy"}
                        </button>
                        <button
                            data-testid="team-invite-dismiss"
                            onClick={() => setInvite(null)}
                            className="rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-500 hover:text-slate-300"
                        >
                            dismiss
                        </button>
                    </div>
                </div>
            )}

            <div className="glass-panel rounded-xl p-6">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <UserPlus className="h-4 w-4 text-cyan-400" />
                    Invite a teammate
                </h3>
                <div className="mt-4 flex flex-wrap gap-2">
                    <input
                        data-testid="team-invite-email-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="teammate@company.com"
                        className={`${inputCls} min-w-0 flex-1`}
                    />
                    <input
                        data-testid="team-invite-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Name (optional)"
                        className={`${inputCls} w-48`}
                    />
                    <button
                        data-testid="team-invite-button"
                        onClick={send}
                        disabled={busy || !email.includes("@")}
                        className="rounded-md bg-cyan-400 px-5 py-2 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                    >
                        {busy ? "Adding…" : "Add member"}
                    </button>
                </div>
            </div>

            <div className="glass-panel rounded-xl p-6">
                <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                    Members
                </h3>
                <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-2.5 font-mono text-[11px]">
                        <span className="text-slate-300">
                            {user?.name || user?.email}
                            <span className="ml-2 rounded border border-cyan-500/30 bg-cyan-950/40 px-1.5 py-px text-[9px] uppercase tracking-wider text-cyan-300">
                                owner
                            </span>
                        </span>
                        <span className="text-slate-600">{user?.email}</span>
                    </div>
                    {members.map((m) => (
                        <div
                            key={m.user_id}
                            data-testid={`team-member-${m.user_id}`}
                            className="flex items-center justify-between rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-2.5 font-mono text-[11px]"
                        >
                            <span className="text-slate-300">
                                {m.name}
                                <span className="ml-2 rounded border border-slate-700 px-1.5 py-px text-[9px] uppercase tracking-wider text-slate-400">
                                    member
                                </span>
                            </span>
                            <span className="flex items-center gap-3">
                                <span className="text-slate-600">{m.email}</span>
                                <button
                                    data-testid={`team-remove-${m.user_id}`}
                                    onClick={() => remove(m.user_id)}
                                    className="flex items-center gap-1 text-slate-500 transition-colors hover:text-rose-300"
                                >
                                    <Trash2 className="h-3 w-3" /> remove
                                </button>
                            </span>
                        </div>
                    ))}
                    {members.length === 0 && (
                        <p className="rounded-lg border border-dashed border-slate-800 py-6 text-center font-mono text-[11px] text-slate-600">
                            No teammates yet — invite your first one above.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
